import { useEffect, useState, useMemo } from "react";
import cx from "classnames";
import Modal from 'react-modal';
import { useQuery } from '@tanstack/react-query'

import Category from "./category";
import CategoriesEditor from "./category-editor";
import { useAppContext } from "./app-context";
import BackArrow from "./back-arrow";

import { fetchCategories, fetchRules, saveRule, deleteRule } from "./api";
import ModalLayout, { smallModal, mediumModal } from "./modal-layout";
import styles from "./rules.module.css";
import CategoryEditor from "./category-editor";
import categories from "./categories";

export const EditRuleModal = ({
    isOpen,
    defaultRule,
    categories,
    onClose,
    onSave
}) => {
    const defaultInitialRule = {
        descriptionMatch: null,
        merchantMatch: null,
        categories: []
    };
    const [editingRule, setEditingRule] = useState(defaultInitialRule);

    useEffect(() => {
        setEditingRule(defaultRule || defaultInitialRule);
    }, [defaultRule]);

    const checkedCategoryMap = useMemo(() => Object
        .fromEntries(editingRule.categories
            .map(c => [c, true])
        ), [editingRule]
    );

    const renderContent = () => {
        if (!categories) {
            return null;
        }
        return (
            <>
                <p>When the transaction matches...</p>
                <label className={styles.matcher}>
                    <input
                        type="checkbox"
                        checked={editingRule.descriptionMatch !== null}
                        onChange={event =>
                            setEditingRule({
                                ...editingRule,
                                descriptionMatch: event.target.checked ? "" : null,
                            })
                        }
                    />
                    <span>Description</span>
                    {editingRule.descriptionMatch !== null &&
                        <input
                            type="text"
                            value={editingRule.descriptionMatch}
                            onChange={e => setEditingRule({
                                ...editingRule,
                                descriptionMatch: e.target.value
                            })}
                        />
                    }
                </label>
                <label className={styles.matcher}>
                    <input
                        type="checkbox"
                        checked={editingRule.merchantMatch !== null}
                        onChange={event =>
                            setEditingRule({
                                ...editingRule,
                                merchantMatch: event.target.checked ? "" : null,
                            })
                        }
                    />
                    <span>Merchant</span>
                    {editingRule.merchantMatch !== null &&
                        <input
                            type="text"
                            value={editingRule.merchantMatch}
                            onChange={e => setEditingRule({
                                ...editingRule,
                                merchantMatch: e.target.value
                            })}
                        />
                    }
                </label>
                <p>... assign the transaction to these categories</p>
                <CategoryEditor
                    categories={categories}
                    checkedCategories={checkedCategoryMap}
                    onCheckedCategoriesChanged={checkedCategories => setEditingRule({
                        ...editingRule,
                        categories: Object.entries(checkedCategories)
                            .filter(([_, c]) => c === true)
                            .map(([c, _]) => c)
                    })}
                />
            </>
        );
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={() => onClose()}
            style={mediumModal}
        >
            <ModalLayout
                title="Edit rule"
                buttons={[{
                    text: "Cancel",
                    onClick: onClose
                }, {
                    text: "Save and Apply",
                    onClick: () => onSave(editingRule, true)
                }, {
                    text: "Save",
                    primary: true,
                    onClick: () => onSave(editingRule, false)
                }]}
                className={styles.modal}
            >
                {renderContent()}
            </ModalLayout>
        </Modal>
    );
};

export default () => {
    const { categoriesQuery, spaceScopedFetch } = useAppContext();
    const categories = categoriesQuery.data;
    const { data: rules, refetch: reloadRules } = useQuery({
        queryKey: [ "rules", spaceScopedFetch?.ident ],
        queryFn: () => fetchRules(spaceScopedFetch),
        placeholderData: []
    });

    const [creatingNewRule, setCreatingNewRule] = useState(false);
    const [editingRule, setEditingRule] = useState(null);

    const closeEditingModal = () => {
        setCreatingNewRule(false);
        setEditingRule(null);
    };

    const ruleDescription = (rule) => {
        if (rule.descriptionMatch) {
            return <b>{rule.descriptionMatch}</b>;
        }
        if (rule.merchantMatch) {
            return <p>{rule.merchantMatch}</p>;
        }
        return <i>No description</i>
    };

    if(!categories) {
        return null;
    }
    const categoriesByUri = Object.fromEntries(categories.map(category => ([category.uri, category])));

    if(!rules) {
        return null;
    }

    return (
        <div className={styles.root}>
            <h1><BackArrow /><span className="material-symbols-outlined">rule</span><span>Rules</span></h1>
            <p>Here you can control how transactions are automatically put into categories.</p>
            <button
                onClick={() => setCreatingNewRule(true)}
            >Create new rule</button>
            <hr/>
            <table>
                <thead>
                    <tr>
                        <th></th>
                        <th></th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {rules.map((rule, index) =>
                        <tr
                            key={index}
                            onClick={() => setEditingRule(rule)}
                        >
                            <td>
                                {ruleDescription(rule)}
                            </td>
                            <td>
                                {rule.categories.map(c =>
                                    <Category
                                        category={categoriesByUri[c]}
                                        checked={true}
                                    />
                                )}
                            </td>
                            <td>
                                <button
                                    onClick={() => setEditingRule(rule)}
                                    className="material-icons"
                                >
                                        edit
                                </button>
                            </td>
                            <td>
                                <button
                                    onClick={async event => {
                                        event.stopPropagation();
                                        if (window.confirm("Are you sure you want to delete this?")) {
                                            await deleteRule(rule.uri, spaceScopedFetch);
                                            await reloadRules();
                                        }
                                    }}
                                    className="material-icons"
                                >delete</button>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            <EditRuleModal
                isOpen={creatingNewRule || editingRule}
                defaultRule={editingRule}
                categories={categories}
                onClose={() => closeEditingModal()}
                onSave={async (newRule, apply) => {
                    await saveRule(newRule, apply, editingRule?.uri, spaceScopedFetch);
                    await reloadRules();
                    closeEditingModal();
                }}
            />
        </div>
    );
};
